import PortalIdParser from 'PortalIdParser';
import { DataFetchingClientProvider } from 'data-fetching-client';
import enviro from 'enviro';
import { PHONE_NUMBER, SEATS_INVENTORY } from 'pricing-pages-api/EarlyRequesterNames';
import { GET_IDENTIFICATION_TOKEN } from 'pricing-pages-api/identification-token/queries';
import { GET_PHONE_NUMBER } from 'pricing-pages-api/phone-number/queries';
import { getInAppLegacyPurchaseMotionsResponse } from 'pricing-pages-api/purchase-motions/getInAppLegacyPurchaseMotionsResponse';
import { GET_SEATS_INVENTORY } from 'pricing-pages-api/seats-inventory/queries';
import { GET_ACTIVE_TRIALS } from 'pricing-pages-api/trials/queries';
import { EN } from 'pricing-pages-lib/constants/Languages';
import { getPreferredLanguage } from 'pricing-pages-lib/utils/getPreferredLanguage';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { getAuth } from 'quartz-core/framework/auth/app';
import { getI18nProvider } from 'quartz-core/framework/i18n';
import Raven from 'raven-js';
import { Provider } from 'react-redux';
import { setPrimaryTracker } from 'usage-tracker-container';
import Routes from './Routes';
import { startApp } from './actions/AppActions';
import { storeEnvironmentConfig } from './actions/ConfigActions';
import { fetchExperiments } from './actions/ExperimentationActions';
import { fetchMarketingContacts } from './actions/MarketingContactsActions';
import { fetchNumContactsInCrm } from './actions/NumContactsInCrmActions';
import { fetchProductInventoryData } from './actions/ProductInventoryActions';
import { fetchProducts } from './actions/ProductsActions';
import { fetchPromotions } from './actions/PromotionActions';
import { fetchPurchaseMotionData } from './actions/PurchaseMotionActions';
import { fetchRequiredOnboarding } from './actions/RequiredOnboardingActions';
import { INITIALIZE_AUTH } from './actions/constants/ActionTypes';
import AsyncDevGateToggle from './components/dev-gate-toggle/AsyncDevGateToggle';
import { DefaultFallbackComponent } from './components/error-boundary/DefaultFallbackComponent';
import ErrorBoundary from './components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from './components/error-boundary/error-severity';
import { APP } from './constants/AuthType';
import { AUTHED_APP } from './constants/ErrorBoundaryNames';
import { CurrencyContextProvider } from './context/CurrencyContext';
import { GlobalLoadingStateContextProvider } from './context/GlobalLoadingStateContext';
import { AuthedI18nContext } from './context/I18nContext';
import { hydrateUpgradePageContext } from './reducers/UpgradePageView';
import { isBillingAdminSelector } from './selectors/AuthSelectors';
import { isHubSpotDotComAuthedUserRedirect } from './utils/appUtils';
import configureStore from './utils/configureStore';
import { dataFetchingClient, dataFetchingClient as dataFetchingClientInstance } from './utils/dataFetchingClient';
import { Metrics, setupMetricsFactory } from './utils/metrics';
import { createUsageTracker } from './utils/tracking/_UsageTracker';
import { trackCommunicationMethodsInteraction } from './utils/tracking/trackingEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function fetchNonAppBlockingPricingAndPurchasingData(store) {
  const reduxFetchRequests = [fetchPurchaseMotionData({
    getPurchaseMotions: () => getInAppLegacyPurchaseMotionsResponse(['MERCH_CARD', 'CALCULATOR', 'HUBSPOT_CREDITS_MERCH_CARD_SMALL', 'HUBSPOT_CREDITS_MERCH_CARD_MEDIUM_LARGE'])
  }), fetchProducts(), fetchPromotions(), fetchNumContactsInCrm(), fetchMarketingContacts(), fetchRequiredOnboarding(), fetchProductInventoryData(), fetchExperiments()].map(fetchRequest => store.dispatch(fetchRequest));
  const preferredLanguage = getPreferredLanguage() || EN;
  const dataFetchingClientFetchRequests = [dataFetchingClient.query({
    query: GET_SEATS_INVENTORY,
    variables: {
      earlyRequestName: SEATS_INVENTORY
    }
  }), dataFetchingClient.query({
    query: GET_ACTIVE_TRIALS
  }), dataFetchingClient.query({
    query: GET_PHONE_NUMBER,
    variables: {
      earlyRequestName: PHONE_NUMBER,
      preferredLanguage
    }
  })];
  return Promise.all([...reduxFetchRequests, ...dataFetchingClientFetchRequests]);
}
export const prepareApp = () => {
  const store = configureStore();
  const tracker = createUsageTracker();
  function loadConversationsScript() {
    // Set the HubSpot Conversations settings
    window.hsConversationsSettings = {
      loadImmediately: false
    };

    // Dynamically create and append the script
    const isQa = enviro.isQa();
    const qaSuffix = isQa ? 'qa' : '';
    const queryParams = getQueryParams();
    let chatPortalId;
    if (isQa) {
      if (queryParams.useDefaultChatPortal !== undefined) {
        chatPortalId = '99535353';
      } else {
        chatPortalId = '100048175';
      }
    } else {
      chatPortalId = '53';
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hubspot-messages-loader';
    script.async = true;
    script.defer = true;
    script.src = `//js.usemessages${qaSuffix}.com/conversations-embed.js`;
    script.setAttribute('data-hsjs-env', enviro.getShort());
    script.setAttribute('data-hsjs-portal', chatPortalId);
    document.body.appendChild(script);
  }
  function setupSentry() {
    const {
      user_id,
      email
    } = getAuth().user;
    if (user_id && email) Raven.setUserContext({
      id: user_id.toString(),
      email
    });
  }
  function setupEventTracker() {
    setPrimaryTracker(tracker);
    const state = store.getState();

    // Required default event properties needs to be set here if we want event tracking on App level - otherwise, it will be undefined and cause usage-tracker-js event error. Properties should not include data from non-blocking fetches as it may not be available when this is called.
    getUsageTracker().setProperties({
      isBillingAdmin: isBillingAdminSelector(state),
      inAppOrPublic: 'in-app',
      isDotComAuthedRedirect: isHubSpotDotComAuthedUserRedirect()
    });
  }

  /**
   * @see {@link https://git.hubteam.com/HubSpot/conversations-visitor/blob/879af5a94b04d87ddf2f6b4dc794d3421bf6a237/docs/external-js-api/ApiReference.md}
   */
  async function setupChat() {
    await initializeChatSettings();
    if (window.HubSpotConversations) {
      onChatWidgetReady();
    } else {
      window.hsConversationsOnReady = [onChatWidgetReady];
    }
  }
  async function initializeChatSettings() {
    const {
      email
    } = getAuth().user;
    try {
      const resp = await dataFetchingClient.query({
        query: GET_IDENTIFICATION_TOKEN
      });
      if (!window.hsConversationsSettings) {
        window.hsConversationsSettings = {
          loadImmediately: false
        };
      }
      const token = resp.data.identificationToken.token;
      if (email && token) {
        window.hsConversationsSettings.identificationEmail = email;
        window.hsConversationsSettings.identificationToken = token;
      }
    } catch (error) {
      Raven.captureException(error, {
        fingerprint: ['failed-to-initialize-chat-settings']
      });
    }
  }
  function addChatWidgetEventTracking() {
    trackCommunicationMethodsInteraction({
      action: 'chat-conversation-loaded',
      trackingSource: 'in-app'
    });
    Metrics.counter('live-chat-loaded', {
      location: 'in-app'
    }).increment();

    /**
     * Conversation started means the user sent a message
     * API reference {@see https://git.hubteam.com/HubSpot/conversations-visitor/blob/master/docs/external-js-api/ApiReference.md}
     */
    window.HubSpotConversations.on('conversationStarted', payload => {
      trackCommunicationMethodsInteraction({
        action: 'chat-conversation-started',
        trackingSource: 'in-app',
        conversationId: payload.conversation.conversationId
      });
      Metrics.counter('live-chat-interaction', {
        location: 'in-app'
      }).increment();
    });
  }
  function onChatWidgetReady() {
    const status = window.HubSpotConversations.widget.status();
    if (!status || !status.pending) {
      window.HubSpotConversations.widget.load();
    }
    addChatWidgetEventTracking();
  }
  function startRouter() {
    fetchNonAppBlockingPricingAndPurchasingData(store).then(() => {
      //temp logic to avoid duplicate fetches to hydrate the upgrade page context
      store.dispatch(hydrateUpgradePageContext());
    }).catch(error => {
      Raven.captureException(error, {
        fingerprint: ['context-setup-error']
      });
      console.error(error);
    });
    setupChat().catch(error => {
      Raven.captureException(error, {
        fingerprint: ['failed-to-setup-chat']
      });
      Metrics.counter('live-chat-failed').increment();
    });
  }
  function start() {
    setupMetricsFactory('pricing-pages-ui');
    loadConversationsScript();
    store.dispatch(startApp());
    enviro.setDebug('laboratory-lib', true);
    store.dispatch({
      type: INITIALIZE_AUTH,
      payload: getAuth()
    });
    store.dispatch(storeEnvironmentConfig({
      authType: APP,
      rootUrl: `/pricing/${PortalIdParser.get()}`,
      // should match deploy yaml
      defaultHubRoute: '/marketing'
    }));

    // await registerSplitLang(lang);

    setupEventTracker();

    // Without setTimeout here so these requests are kicked off before the iframe is loaded so they do not hit CORS
    // and do end up generating OPTIONS requests. This setTimeout attempts to delay the fetches just long enough for it to return.
    setTimeout(() => {
      try {
        setupSentry();
        startRouter();
      } catch (__error) {
        // We chain our bootstrapping code with this catch
        // so we can show an Error screen rather than just a blank page
        // A side effect of this is that any errors that bubble up here
        // would be swallowed up unless we re-throw.
        startRouter();
        // Raven.captureException(new Error('Failed to bootstrap in-app'), {
        //   extra: { error },
        // });
      }
    }, 0);
    return {
      props: {
        dataFetchingClient: dataFetchingClientInstance,
        store
      }
    };
  }
  return start();
};
export function AppComponent(props) {
  const DevGateToggleComponent = enviro.isQa() ? AsyncDevGateToggle : null;
  const i18nLoader = getI18nProvider();
  return /*#__PURE__*/_jsx(DataFetchingClientProvider, {
    client: props.dataFetchingClient,
    children: /*#__PURE__*/_jsx(Provider, {
      store: props.store,
      children: /*#__PURE__*/_jsx(AuthedI18nContext.Provider, {
        value: {
          i18nLoader: i18nLoader
        },
        children: /*#__PURE__*/_jsx(GlobalLoadingStateContextProvider, {
          children: /*#__PURE__*/_jsx(CurrencyContextProvider, {
            children: /*#__PURE__*/_jsxs(ErrorBoundary, {
              errorBoundaryName: AUTHED_APP,
              FallbackComponent: DefaultFallbackComponent,
              severity: ERROR_SEVERITY.HIGH,
              children: [/*#__PURE__*/_jsx(Routes, {}), DevGateToggleComponent && /*#__PURE__*/_jsx(DevGateToggleComponent, {})]
            })
          })
        })
      })
    })
  });
}